import { Alert, Button, Input, Modal } from "antd";
import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSolaCardFields from "../../hooks/useSolaCardFields";
import { chargeSolaReservation, newIdempotencyKey } from "../../lib/sola/solaPaymentApi";

const IFRAME_CLASS = "h-[40px] w-full rounded-md border border-[#d9d9d9] bg-white";

/** "1230" from "12/30", "12 / 30", "1230". */
const toXExp = (value) => value.replace(/\D/g, "").slice(0, 4);
const formatExpiry = (value) => {
    const digits = toXExp(value);
    return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
};

/**
 * Takes a card in admin and charges it through Sola in one step.
 *
 * The card number and CVV are Sola iframes (their data-ifields-id values are fixed by Sola's
 * script). destroyOnClose remounts them on every open, which is why useSolaCardFields waits for
 * the frames before binding them.
 */
const SolaCardPaymentModal = ({
    open,
    amount,
    reservationId,
    reservationProcess,
    customerEmail,
    extensionDropoffDate,
    companyId,
    onPaid,
    onCancel,
}) => {
    const { t } = useTranslation("reservationPayment");
    const { ready, error: fieldsError, iframeSrc, getCardTokens } = useSolaCardFields({ enabled: open });

    const [expiry, setExpiry] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Kept across a retry that never got an answer (so it replays instead of charging twice),
    // replaced after anything the server answered — a declined key is spent.
    const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

    const amountLabel = useMemo(
        () => Number(amount || 0).toLocaleString(undefined, { style: "currency", currency: "USD" }),
        [amount],
    );

    const handlePay = useCallback(async () => {
        setError(null);
        const xExp = toXExp(expiry);
        if (xExp.length !== 4) {
            setError(t("solaCard.expiryInvalid"));
            return;
        }

        setSubmitting(true);
        try {
            const { sut, xCVV } = await getCardTokens();
            const result = await chargeSolaReservation({
                reservationId,
                reservationProcess,
                amount,
                idempotencyKey,
                sut,
                xCVV,
                xExp,
                customerEmail,
                extensionDropoffDate,
                companyId,
            });
            onPaid(result);
        } catch (err) {
            if (err?.response) {
                setIdempotencyKey(newIdempotencyKey());
            }
            setError(err?.response?.data?.message || err?.message || t("solaCard.failed"));
            setSubmitting(false);
        }
    }, [
        amount,
        companyId,
        customerEmail,
        expiry,
        extensionDropoffDate,
        getCardTokens,
        idempotencyKey,
        onPaid,
        reservationId,
        reservationProcess,
        t,
    ]);

    return (
        <Modal
            title={t("solaCard.title")}
            open={open}
            onCancel={submitting ? undefined : onCancel}
            maskClosable={false}
            closable={!submitting}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={onCancel} disabled={submitting}>
                    {t("solaCard.cancel")}
                </Button>,
                <Button key="pay" type="primary" loading={submitting} disabled={!ready} onClick={handlePay}>
                    {submitting ? t("solaCard.processing") : t("solaCard.pay", { amount: amountLabel })}
                </Button>,
            ]}>
            <p className="mb-4 text-sm text-gray-500">{t("solaCard.paying", { amount: amountLabel })}</p>

            {fieldsError && <Alert className="mb-3" type="error" message={fieldsError} showIcon />}
            {error && <Alert className="mb-3" type="error" message={error} showIcon />}

            <label className="mb-1 block text-sm font-medium" htmlFor="sola-card-number">
                {t("solaCard.cardNumber")}
            </label>
            <iframe
                id="sola-card-number"
                title={t("solaCard.cardNumber")}
                data-ifields-id="card-number"
                src={iframeSrc}
                className={IFRAME_CLASS}
            />
            <input data-ifields-id="card-number-token" type="hidden" />

            <div className="mt-3 flex gap-3">
                <div className="flex-1">
                    <label className="mb-1 block text-sm font-medium" htmlFor="sola-expiry">
                        {t("solaCard.expiry")}
                    </label>
                    <Input
                        id="sola-expiry"
                        inputMode="numeric"
                        placeholder={t("solaCard.expiryPlaceholder")}
                        value={expiry}
                        onChange={(event) => setExpiry(formatExpiry(event.target.value))}
                        disabled={submitting}
                    />
                </div>
                <div className="flex-1">
                    <label className="mb-1 block text-sm font-medium" htmlFor="sola-cvv">
                        {t("solaCard.cvv")}
                    </label>
                    <iframe
                        id="sola-cvv"
                        title={t("solaCard.cvv")}
                        data-ifields-id="cvv"
                        src={iframeSrc}
                        className={IFRAME_CLASS}
                    />
                    <input data-ifields-id="cvv-token" type="hidden" />
                </div>
            </div>

            <p className="mt-4 text-xs text-gray-400">{t("solaCard.secureNote")}</p>
        </Modal>
    );
};

SolaCardPaymentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    amount: PropTypes.number.isRequired,
    reservationId: PropTypes.string.isRequired,
    reservationProcess: PropTypes.string.isRequired,
    customerEmail: PropTypes.string,
    extensionDropoffDate: PropTypes.string,
    companyId: PropTypes.string,
    onPaid: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

export default SolaCardPaymentModal;
